import { useRef, useEffect, useState } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValueEvent,
    useReducedMotion,
} from "framer-motion";
import { sceneRefs } from "@/state/sceneRefs";

/**
 * EarthShowcase — a transparent scroll-driven section that lets the persistent
 * 3D globe take centre stage. The section writes scroll progress into
 * `sceneRefs.globeFocus` (0..1) which the Scene3D camera rig blends toward.
 *
 * Visually: a 110vh tall section with a sticky 100vh child. The child contains
 * only overlay typography (no 2D background) so the 3D globe behind shines through.
 */

const COORDS = [
    { name: "Kuala Lumpur", val: "03.1390° N · 101.6869° E" },
    { name: "Singapore", val: "01.3521° N · 103.8198° E" },
    { name: "Shanghai", val: "31.2304° N · 121.4737° E" },
    { name: "Dubai", val: "25.2048° N · 055.2708° E" },
    { name: "Istanbul", val: "41.0082° N · 028.9784° E" },
    { name: "Doha", val: "25.2854° N · 051.5310° E" },
    { name: "Sydney", val: "33.8688° S · 151.2093° E" },
    { name: "Toronto", val: "43.6532° N · 079.3832° W" },
];

export const EarthShowcase = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    // Active-while-section-is-in-view window
    const focus = useTransform(
        scrollYProgress,
        [0.15, 0.4, 0.6, 0.85],
        [0, 1, 1, 0],
    );

    // Coordinate ticker
    const [cursor, setCursor] = useState(0);
    useMotionValueEvent(scrollYProgress, "change", (v) => {
        sceneRefs.globeFocus = focus.get();
        sceneRefs.globeBoost = focus.get() * 1.4;
        const idx = Math.min(
            COORDS.length - 1,
            Math.floor(((v - 0.15) / 0.7) * COORDS.length),
        );
        if (idx >= 0 && idx !== cursor) setCursor(idx);
    });

    // Reset on unmount
    useEffect(() => {
        return () => {
            sceneRefs.globeFocus = 0;
            sceneRefs.globeBoost = 0;
        };
    }, []);

    // Headline morph: "ONE WORLD." (0..0.5) → "ONE NETWORK." (0.5..1)
    const oneOpacity = useTransform(
        scrollYProgress,
        [0.15, 0.28, 0.46, 0.55],
        [0, 1, 1, 0],
    );
    const oneY = useTransform(
        scrollYProgress,
        [0.15, 0.28, 0.46, 0.55],
        ["20%", "0%", "0%", "-20%"],
    );
    const twoOpacity = useTransform(
        scrollYProgress,
        [0.5, 0.58, 0.78, 0.86],
        [0, 1, 1, 0],
    );
    const twoY = useTransform(
        scrollYProgress,
        [0.5, 0.58, 0.78, 0.86],
        ["20%", "0%", "0%", "-20%"],
    );
    const labelOpacity = useTransform(
        scrollYProgress,
        [0.1, 0.25, 0.8, 0.92],
        [0, 1, 1, 0],
    );
    const ringScale = useTransform(scrollYProgress, [0.15, 0.85], [0.85, 1.35]);
    const ringOpacity = useTransform(
        scrollYProgress,
        [0.15, 0.3, 0.7, 0.85],
        [0, 0.5, 0.5, 0],
    );

    const c = COORDS[cursor];

    return (
        <section
            ref={ref}
            id="globe-showcase"
            data-testid="globe-showcase-section"
            aria-label="Global network showcase"
            className="relative"
            style={{ height: reduce ? "auto" : "110vh" }}
        >
            <div
                className={`${
                    reduce ? "" : "sticky top-0"
                } h-screen w-full overflow-hidden flex flex-col`}
            >
                {/* Vignette so the globe in the persistent 3D bg pops */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse 60% 70% at 50% 55%, rgba(5,5,5,0) 0%, rgba(5,5,5,0.55) 65%, rgba(5,5,5,0.95) 95%)",
                    }}
                />

                {/* Expanding ring around globe */}
                {!reduce && (
                    <motion.div
                        aria-hidden
                        style={{
                            scale: ringScale,
                            opacity: ringOpacity,
                        }}
                        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vmin] h-[60vmin]"
                    >
                        <div className="w-full h-full rounded-full border border-[#D69331]/40" />
                        <div className="absolute inset-6 rounded-full border border-white/8 border-dashed" />
                    </motion.div>
                )}

                {/* Top-left label */}
                <motion.div
                    style={{ opacity: labelOpacity }}
                    className="relative z-10 pt-28 sm:pt-32 px-4 sm:px-8 lg:px-12"
                >
                    <div className="flex items-center gap-3 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-400">
                        <span className="inline-block w-2 h-2 bg-[#D69331] animate-pulse" />
                        <span>Interlude</span>
                        <span className="h-px w-10 bg-[#D69331]" />
                        <span>Global Network — Live</span>
                    </div>
                </motion.div>

                {/* Centered morphing headline */}
                <div className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-8 lg:px-12">
                    <div className="relative w-full text-center">
                        <motion.h2
                            data-testid="globe-showcase-text-one"
                            style={
                                reduce
                                    ? {}
                                    : { opacity: oneOpacity, y: oneY }
                            }
                            className="absolute inset-x-0 font-display uppercase font-bold leading-[0.85] tracking-[-0.03em] text-white text-[14vw] sm:text-[13vw] lg:text-[11vw] drop-shadow-[0_4px_30px_rgba(0,0,0,0.65)]"
                        >
                            One World<span className="text-[#D69331]">.</span>
                        </motion.h2>
                        <motion.h2
                            data-testid="globe-showcase-text-two"
                            style={
                                reduce
                                    ? {}
                                    : { opacity: twoOpacity, y: twoY }
                            }
                            className="absolute inset-x-0 font-display uppercase font-bold leading-[0.85] tracking-[-0.03em] text-white text-[14vw] sm:text-[13vw] lg:text-[11vw] drop-shadow-[0_4px_30px_rgba(0,0,0,0.65)]"
                        >
                            One <span className="text-[#D69331]">Network.</span>
                        </motion.h2>
                    </div>
                </div>

                {/* Bottom — Coordinate ticker */}
                <motion.div
                    style={{ opacity: labelOpacity }}
                    className="relative z-10 px-4 sm:px-8 lg:px-12 pb-10 sm:pb-14"
                >
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-t border-white/10 pt-5">
                        <div className="flex items-baseline gap-4">
                            <span className="font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                                Now tracking
                            </span>
                            <motion.span
                                key={c.name}
                                initial={{ y: 6, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.35 }}
                                className="font-display uppercase text-xl sm:text-2xl lg:text-3xl font-semibold text-white"
                            >
                                {c.name}
                            </motion.span>
                        </div>
                        <motion.span
                            key={c.val}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                            className="font-mono-text text-xs sm:text-sm text-[#D69331]"
                        >
                            {c.val}
                        </motion.span>
                        <span className="font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                            {String(cursor + 1).padStart(2, "0")} / {String(COORDS.length).padStart(2, "0")}
                        </span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
